/**
 * Modulation recognition — "What was the modulation?"
 *
 * Plays a brief I-IV-V-I cadence in the host key, then jumps directly
 * (no pivot, no connecting cadence) into a I-IV-V-I cadence in the new
 * key. Per spec §4.4, the cadence between the two key sections is
 * deliberately omitted — the user identifies the relationship by hearing
 * the two tonics back-to-back. Voice leading resets at the modulation
 * point so the shift is unambiguous; nothing tries to smooth over the
 * key change.
 *
 * Five relationships, all from a major host:
 *   - relative   — vi minor (same key signature, opposite mode)
 *   - parallel   — same tonic, opposite mode
 *   - dominant   — IV→I motion to the V key (up a P5)
 *   - subdominant — down a P5 to the IV key
 *   - mediant    — up a M3 (chromatic mediant) — the most striking
 *
 * Wrong-answer teach plan: replays just the modulation transition (last
 * host chord → first new-key tonic) at slow tempo with the new tonic
 * highlighted, so the relationship is heard in isolation.
 */

import { AudioPlanBuilder, type AudioPlan } from "../audio-plan.js";
import type { Exercise, AnswerOption } from "../exercise.js";
import { Rng } from "../exercise.js";
import {
  buildChord,
  degreeOffset,
  DIATONIC_TRIAD_QUALITIES,
  pickKey,
  type Mode,
} from "../utils.js";

export type ModulationRelationship =
  | "relative"
  | "parallel"
  | "dominant"
  | "subdominant"
  | "mediant";

interface RelationshipSpec {
  /** Semitone offset of the new tonic from the host tonic. */
  semitones: number;
  /** Mode of the new key. (Host is always ionian/major.) */
  targetMode: Mode;
  /** Label shown on the answer button. */
  label: string;
}

const RELATIONSHIPS: Record<ModulationRelationship, RelationshipSpec> = {
  // Relative minor: vi minor of the host. Same key signature, different tonic.
  relative: { semitones: 9, targetMode: "aeolian", label: "Relative minor" },
  // Parallel minor: same tonic, opposite mode.
  parallel: { semitones: 0, targetMode: "aeolian", label: "Parallel minor" },
  // Dominant: up a P5 (V of host becomes I).
  dominant: { semitones: 7, targetMode: "ionian", label: "Dominant (up a 5th)" },
  // Subdominant: up a P4 (IV of host becomes I).
  subdominant: { semitones: 5, targetMode: "ionian", label: "Subdominant (up a 4th)" },
  // Chromatic mediant: up a M3.
  mediant: { semitones: 4, targetMode: "ionian", label: "Chromatic mediant (up a 3rd)" },
};

const ALL_RELATIONSHIPS: ModulationRelationship[] = [
  "relative",
  "parallel",
  "dominant",
  "subdominant",
  "mediant",
];

export interface ModulationParams {
  relationship: ModulationRelationship;
}

/**
 * Voice a triad against the previous voicing.
 *
 * Modulation can shift the new key into a higher register (relative is
 * up a m6, dominant up a P5), which would push the upper voice past the
 * shared `smoothVoicing` MIDI ceiling of 76. This local helper widens
 * the range to 36..88 and falls back to close root position if no
 * voicing fits — losing voice leading at that point is fine because the
 * pedagogical goal is to expose the key shift, not hide it.
 */
function voiceTriad(prev: number[] | null, notes: number[]): number[] {
  if (!prev) {
    const [root, third, fifth] = notes as [number, number, number];
    const top = fifth < root + 7 ? fifth + 12 : fifth;
    return [root, third, top];
  }
  const candidates: number[][] = [];
  for (const o1 of [-12, 0, 12]) {
    for (const o2 of [-12, 0, 12]) {
      for (const o3 of [-12, 0, 12]) {
        const v = [notes[0]! + o1, notes[1]! + o2, notes[2]! + o3].sort((a, b) => a - b);
        if (v[0]! >= 36 && v[v.length - 1]! <= 88) candidates.push(v);
      }
    }
  }
  if (candidates.length === 0) {
    const [root, third, fifth] = notes as [number, number, number];
    const top = fifth < root + 7 ? fifth + 12 : fifth;
    return [root, third, top];
  }
  let best = candidates[0]!;
  let bestCost = Infinity;
  for (const v of candidates) {
    let cost = 0;
    for (let i = 0; i < v.length; i++) cost += Math.abs(v[i]! - prev[i]!);
    if (cost < bestCost) {
      bestCost = cost;
      best = v;
    }
  }
  return best;
}

/**
 * Append a I-IV-V-I cadence in (tonicMidi, mode) to the builder.
 * Returns the final voicing so the caller can decide whether to chain
 * voice-leading into the next section (we don't, at the modulation point).
 */
function appendKeyCadence(
  builder: AudioPlanBuilder,
  tonicMidi: number,
  mode: Mode,
  prev: number[] | null,
  opts: { dur: number; gap: number; finalDur?: number },
): number[] {
  const qualities = DIATONIC_TRIAD_QUALITIES[mode];
  const degrees = [1, 4, 5, 1];
  let v: number[] | null = prev;
  for (let i = 0; i < degrees.length; i++) {
    const deg = degrees[i]!;
    const rootMidi = tonicMidi + degreeOffset(deg, mode);
    const triad = buildChord(rootMidi, qualities[deg - 1]!);
    const voicing = voiceTriad(v, triad);
    const isFinal = i === degrees.length - 1;
    const dur = isFinal ? (opts.finalDur ?? opts.dur) : opts.dur;
    builder.addNote(rootMidi - 12, dur, 0.11, "bass");
    builder.addChord(voicing, dur, 0.07, "harmony");
    builder.advance(dur + opts.gap);
    v = voicing;
  }
  return v!;
}

export function generateModulation(
  itemId: string,
  params: ModulationParams,
  seed: number,
): Exercise {
  const rng = new Rng(seed);
  const hostKey = pickKey(rng, "major");

  const spec = RELATIONSHIPS[params.relationship];
  const newTonicMidi = hostKey.tonicMidi + spec.semitones;

  const builder = new AudioPlanBuilder();

  // ── Host-key cadence (I-IV-V-I) ──────────────────────────────────────
  const hostDur = 0.85;
  const hostGap = 0.05;
  appendKeyCadence(builder, hostKey.tonicMidi, hostKey.mode, null, {
    dur: hostDur,
    gap: hostGap,
    finalDur: hostDur * 1.1,
  });

  // ── Modulation: cadence omitted, brief breath, voice leading resets ──
  builder.advance(0.45);

  // ── New-key cadence (I-IV-V-I in the new key) ───────────────────────
  // Pass `prev=null` so voice leading restarts cleanly — this is the
  // pedagogical signal that "we are now in a different key".
  appendKeyCadence(builder, newTonicMidi, spec.targetMode, null, {
    dur: hostDur,
    gap: hostGap,
    finalDur: hostDur * 1.4,
  });

  builder.advance(0.5);
  const audio = builder.build();

  // Options: all five relationships, shuffled.
  const optionRels = rng.shuffle(ALL_RELATIONSHIPS);
  const options: AnswerOption[] = optionRels.map((r) => ({
    id: r,
    label: RELATIONSHIPS[r].label,
  }));

  return {
    itemId,
    exerciseType: "modulation-recognition",
    audio,
    prompt: "What kind of modulation did you hear?",
    options,
    correctAnswerId: params.relationship,
    meta: {
      keyName: hostKey.name,
      seed,
      explanation: `${RELATIONSHIPS[params.relationship].label} — host ${hostKey.name} modulating to ${spec.targetMode === "aeolian" ? "minor" : "major"} key ${spec.semitones} semitones away.`,
      distractorCount: options.length - 1,
      teachAudio: buildModulationTeachPlan(
        hostKey.tonicMidi,
        hostKey.mode,
        newTonicMidi,
        spec.targetMode,
      ),
    },
  };
}

/**
 * Wrong-answer teach plan: replay just the transition — host I, then
 * new-key I (highlighted as `target`) — slowly, so the listener can
 * compare the two tonics directly. Then play the new-key cadence to
 * confirm the new tonic is, in fact, the tonic.
 */
function buildModulationTeachPlan(
  hostTonicMidi: number,
  hostMode: Mode,
  newTonicMidi: number,
  newMode: Mode,
): AudioPlan {
  const builder = new AudioPlanBuilder();
  const dur = 1.6;
  const gap = 0.3;

  // Host I — so the ear has a reference.
  const hostQ = DIATONIC_TRIAD_QUALITIES[hostMode][0]!;
  const hostI = buildChord(hostTonicMidi, hostQ);
  const hostVoicing = voiceTriad(null, hostI);
  builder.addNote(hostTonicMidi - 12, dur, 0.13, "bass");
  builder.addChord(hostVoicing, dur, 0.085, "harmony");
  builder.advance(dur + gap);

  // New-key I, tagged `target` so the engine can highlight it.
  const newQ = DIATONIC_TRIAD_QUALITIES[newMode][0]!;
  const newI = buildChord(newTonicMidi, newQ);
  const newVoicing = voiceTriad(null, newI);
  builder.addNote(newTonicMidi - 12, dur, 0.13, "bass");
  builder.addChord(newVoicing, dur, 0.085, "target");
  builder.advance(dur + gap);

  // Slow new-key cadence so the new tonic settles.
  appendKeyCadence(builder, newTonicMidi, newMode, null, {
    dur: dur,
    gap: 0.2,
    finalDur: dur * 1.3,
  });

  builder.advance(0.4);
  return builder.build();
}

/** Exposed for tests so they don't have to redo the math. */
export const MODULATION_RELATIONSHIPS = RELATIONSHIPS;
